import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationWorkflowService } from 'src/app/services/navigation-workflow.service';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { forkJoin, merge, of, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';
import { WebsiteAnalyzeService } from 'src/app/services/common/website-analyze/website-analyze.service';
import { MatRadioModule } from '@angular/material/radio';
import { MatDialog } from '@angular/material/dialog';
import { ShowDetailsWorkflowComponent } from '../show-details-workflow/show-details-workflow.component';
// --- amCharts ---
import * as am5 from "@amcharts/amcharts5";
import * as am5percent from "@amcharts/amcharts5/percent";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { SenderVarService } from 'src/app/services/senderVar.service';
import { ConstantService } from 'src/app/shared/constant/constant.service';

@Component({
    selector: 'app-rum-workflow',
    standalone : true,
    imports: [CommonModule, MatSelectModule, FormsModule, MatIconModule, MatRadioModule],
    templateUrl: './rum-workflow.component.html',
    styleUrls: ['./rum-workflow.component.scss']
})
export class RumWorkflowComponent implements OnInit, OnDestroy {
  userId: string;
  workflows: any[] = [];
  selectedWorkflow: any;
  rumData: any[] = [];
  validRumData: any[] = [];

  /** Start true so we never show "No journeys" while the first request is in flight. */
  loading = true;
  errorMsg = '';
  chartErrorMsg = '';

  selectedDevice: string = 'desktop';
  selectedQueryInterval: string = 'daily'; // daily, weekly, monthly


  // amCharts root
  private root!: am5.Root;
  private projectRouteSub?: Subscription;

  constructor(
    private workflowService: NavigationWorkflowService,
    private websiteAnalyzeService: WebsiteAnalyzeService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private senderVarService: SenderVarService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.userId =
      localStorage.getItem(ConstantService.localStorageKeys.userId) ||
      localStorage.getItem('userId') ||
      '';
    const id$ = this.route.parent?.paramMap ?? this.route.paramMap;
    const nav$ = this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
    );
    this.projectRouteSub = merge(id$, nav$)
      .pipe(
        map(() =>
          [
            this.senderVarService.resolveProjectIdFrom(this.route, this.router) ||
              '',
            this.router.url.split('?')[0],
          ].join('|'),
        ),
        distinctUntilChanged(),
      )
      .subscribe(() => this.loadRumWorkflows());
  }

  loadRumWorkflows(): void {
    if (!this.userId) {
      this.errorMsg = 'No userId found in localStorage.';
      this.loading = false;
      this.cdr.markForCheck();
      return;
    }
    this.loading = true;
    this.errorMsg = '';
    this.chartErrorMsg = '';

    const routeProjectId =
      this.senderVarService.syncProjectIdFromRoute(this.route, this.router) ??
      this.senderVarService.resolveProjectIdFrom(this.route, this.router);

    // Get all projects for this user first (like in my-account-page)
    this.websiteAnalyzeService.getWebsiteAnalyze(this.userId).subscribe({
      next: (websites) => {
        const siteList = Array.isArray(websites) ? websites : [websites];
        const scoped =
          routeProjectId != null && routeProjectId !== ''
            ? siteList.filter((w) => w?.id === routeProjectId)
            : siteList;
        const workflowRequests = scoped.map((website) =>
          this.workflowService.getNavigationWorkflows(website.id).pipe(
            catchError((err) => {
              console.error(
                `Error loading workflows for project ${website.id}:`,
                err,
              );
              return of([]);
            }),
          ),
        );

        if (workflowRequests.length === 0) {
          this.workflows = [];
          this.selectedWorkflow = undefined;
          this.rumData = [];
          this.validRumData = [];
          this.loading = false;
          this.cdr.markForCheck();
          return;
        }

        forkJoin(workflowRequests).subscribe({
          next: (results) => {
            const allWorkflows = ([] as any[]).concat(...results);
            this.workflows = allWorkflows.filter((wf) => wf.type === 'RUM');

            if (this.workflows.length > 0) {
              this.selectedWorkflow = this.workflows[0];
              this.loadRumDataForWorkflow(this.selectedWorkflow);
            } else {
              this.selectedWorkflow = undefined;
              this.rumData = [];
              this.validRumData = [];
              this.loading = false;
              this.cdr.markForCheck();
            }
          },
          error: (err) => {
            console.error('Error loading workflows:', err);
            this.errorMsg = 'Error loading RUM workflows.';
            this.loading = false;
            this.cdr.markForCheck();
          },
        });
      },
      error: (err) => {
        console.error('Error loading websites:', err);
        this.errorMsg = 'Error loading websites.';
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  selectWorkflow(wf: any): void {
    this.selectedWorkflow = wf;
    this.loadRumDataForWorkflow(wf);
  }

  loadRumDataForWorkflow(workflow: any): void {
    if (!workflow || !workflow.pages?.length) {
      this.rumData = [];
      this.validRumData = [];
      this.loading = false;
      this.cdr.markForCheck();
      return;
    }
    const pages = workflow.pages;

    const observables = pages.map((p: { url: string }) =>
      this.websiteAnalyzeService.getRumDocWithUxr(this.userId, p.url, this.selectedDevice, this.selectedQueryInterval).pipe(
        catchError(err => {
          console.error('Error retrieving RUM doc for url=', p.url, err);
          return of({ url: p.url, error: 'Error retrieving RUM doc' });
        }),
        map((doc: any) => {
          if (doc && doc.uxr !== undefined) {
            return { url: p.url, doc };
          } else {
            return { url: p.url, error: 'No RUM data found' };
          }
        })
      )
    );

    if (observables.length === 0) {
      this.rumData = [];
      this.validRumData = [];
      this.loading = false;
      this.cdr.markForCheck();
      return;
    }

    forkJoin(observables).subscribe({
      next: (results: any[]) => {
        this.rumData = results;
        this.validRumData = this.rumData.filter((item) => !item.error);

        const invalidUrls = this.rumData
          .filter((item) => item.error)
          .map((item) => item.url);
        this.chartErrorMsg = invalidUrls.length
          ? 'The following URLs have no RUM data: ' + invalidUrls.join(', ')
          : '';

        this.loading = false;
        this.cdr.markForCheck();

        setTimeout(() => {
          this.buildAmChartFunnel();
        });
      },
      error: () => {
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  buildAmChartFunnel(): void {
    if (this.root) {
      this.root.dispose();
    }

    // Créer la racine
    this.root = am5.Root.new("chartdiv");
    this.root.setThemes([am5themes_Animated.new(this.root)]);

    // Créer le SlicedChart
    const chart = this.root.container.children.push(
      am5percent.SlicedChart.new(this.root, {
        layout: this.root.verticalLayout
      })
    );

    // Créer la série Funnel
    const series = chart.series.push(
      am5percent.FunnelSeries.new(this.root, {
        valueField: "value",
        categoryField: "category",
        orientation: "horizontal",
        bottomRatio: 1,
        alignLabels: false
      })
    );

    // Préparer les données
    const chartData = this.validRumData.map(item => ({
      category: item.url,
      value: item.doc?.uxr || 0
    }));
    series.data.setAll(chartData);

    // Rendre les slices interactives
    series.slices.template.setAll({
      interactive: true,
      cursorOverStyle: "pointer"
    });

    // 8. Écouter l'événement "click" sur les slices
    series.events.on("datavalidated", () => {
      series.dataItems.forEach((dataItem) => {
        const slice = dataItem.get("slice");
        if (slice) {
          slice.setAll({
            interactive: true,
            cursorOverStyle: "pointer"
          });
          slice.events.on("click", () => {
            const dataContext = dataItem.dataContext as { category: string; value: number };
            this.openModal(dataContext.category);
          });
        }
      });
    });


    // Label "UXR : {value}"
    series.labels.template.setAll({
      text: "UXR : {value}%",
      interactive: false,
      rotation: 0,
      textAlign: "start"
    });

    // Légende
    const legend = chart.children.push(
      am5.Legend.new(this.root, {
        centerX: am5.p50,
        x: am5.p50,
        marginTop: 15,
        marginBottom: 15
      })
    );
    legend.labels.template.set("text", "{category}");
    legend.valueLabels.template.setAll({ forceHidden: true });
    legend.data.setAll(series.dataItems);

    // Animation
    series.appear();
    chart.appear(1000, 100);
  }

  onFilterChange(): void {
    if (this.selectedWorkflow) {
      this.loadRumDataForWorkflow(this.selectedWorkflow);
    }
  }
  openModal(url: string): void {
    const found = this.rumData.find(d => d.url === url);
    if (!found || !found.doc || !found.doc.metricData) {
      return;
    }


    const metricDataArray = found.doc.metricData
    const calculatedMetrics = metricDataArray[0].calculated;


    const lcpObj = calculatedMetrics.find((m: any) => m._id === 'LCP');
    const clsObj = calculatedMetrics.find((m: any) => m._id === 'CLS');
    const inpObj = calculatedMetrics.find((m: any) => m._id === 'INP');



    const lcpData = lcpObj ? {
      good: (lcpObj.pctGood * 100).toFixed(2),
      need: (lcpObj.pctNeedsImprovement * 100).toFixed(2),
      poor: (lcpObj.pctPoor * 100).toFixed(2),
      p75: lcpObj.percentiles[1].toFixed(2)
    } : null;

    const clsData = clsObj ? {
      good: (clsObj.pctGood * 100).toFixed(2),
      need: (clsObj.pctNeedsImprovement * 100).toFixed(2),
      poor: (clsObj.pctPoor * 100).toFixed(2),

      p75: (clsObj.percentiles[1]).toFixed(2)
    } : null;

    const inpData = inpObj ? {
      good: (inpObj.pctGood * 100).toFixed(2),
      need: (inpObj.pctNeedsImprovement * 100).toFixed(2),
      poor: (inpObj.pctPoor * 100).toFixed(2),
      p75: inpObj.percentiles[1].toFixed(2)
    } : null;

    const modalData = {
      url,
      device: found.doc.device,
      lcpData,
      clsData,
      inpData,

    };

    this.dialog.open(ShowDetailsWorkflowComponent, {
      width: '1000px',
      height: '500px',
      data: modalData
    });
  }

  ngOnDestroy(): void {
    this.projectRouteSub?.unsubscribe();
    if (this.root) {
      this.root.dispose();
    }
  }

  navigateToMyAccount() {
    this.router.navigate(['/home/myAccount'], { queryParams: { tab: 3 } });
  }
}
